import "./lib/fetchHeaderSanitizer";
import { createRoot } from "react-dom/client";
import { initSentry } from "./lib/sentry";
import { initPerformanceMonitoring } from "./lib/performance";
import {
  nukeStaleWorkers,
  cleanupPWAInPreview,
  isControlledByServiceWorker,
  isPreviewOrIframe,
} from "./lib/pwa";
import "./lib/i18n";
import "./index.css";
import App from "./App";

// Remove any legacy caching service workers left by old builds.
// In the Lovable preview/iframe we run an extra-aggressive pass that also
// kills the push worker, since nothing should ever intercept requests in
// the editor preview.
const RELOAD_KEY = "sw-reset-reload-count";
const MAX_RELOADS = 2;

async function bootstrap() {
  let cleaned = await nukeStaleWorkers();
  if (isPreviewOrIframe) {
    const previewCleaned = await cleanupPWAInPreview();
    cleaned = cleaned || previewCleaned;
  }

  // If a SW is still controlling this page, the current document was served
  // by the (now-unregistered) worker. We MUST reload to get a fresh shell.
  const stillControlled = isControlledByServiceWorker();
  const needsReload = cleaned || stillControlled;

  if (needsReload) {
    const count = Number(sessionStorage.getItem(RELOAD_KEY) ?? "0");
    if (count < MAX_RELOADS) {
      sessionStorage.setItem(RELOAD_KEY, String(count + 1));
      window.location.reload();
      return;
    }
    // Give up after MAX_RELOADS to avoid infinite loops; render anyway.
    sessionStorage.removeItem(RELOAD_KEY);
  } else {
    sessionStorage.removeItem(RELOAD_KEY);
  }

  initSentry();
  initPerformanceMonitoring();

  const rootElement = document.getElementById("root");
  if (!rootElement) throw new Error("Root element not found");

  const root = createRoot(rootElement);
  root.render(<App />);
}

bootstrap();
